import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Plus, Edit, Trash2, Eye, TrendingUp } from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { toastErrorOnce } from '../utils/permissionToast';
import Pagination from '../components/Pagination';
import CFiltersBar from '../components/shared/c-filters-bar';
import CTable from '../components/shared/c-table';
import SummaryCard from '../components/shared/summary-card';
import CModal from '../components/shared/c-modal';
import { buildQueryParams } from '../utils/query';
import { EMPLOYEE_STATUSES, EMPLOYMENT_TYPES } from '../constants';
import { formatCurrency } from '../utils/currency';
import { getStatusBadgeClasses, formatStatusText } from '../utils/statusUtils';
import { useAuth } from '../contexts/AuthContext';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import { canEditSalaries } from '../utils/compensationPermissions';
import { formatDateOnly } from '../utils/dateUtils';

const Salaries = () => {
  const { hasPermission, user } = useAuth();
  // Same gate as EditSalary: salaries:update_own (held by every employee) is not enough.
  const canEdit = canEditSalaries(hasPermission);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = usePersistedState('salaries.search', '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [currencyFilter, setCurrencyFilter] = usePersistedState(
    'salaries.currency',
    ''
  );
  const [employmentTypeFilter, setEmploymentTypeFilter] = usePersistedState(
    'salaries.employmentType',
    ''
  );
  const [departmentFilter, setDepartmentFilter] = usePersistedState(
    'salaries.department',
    ''
  );
  // Filters the EMPLOYEE's employment_status - the field the Status column
  // renders. Defaults to active rather than showing everyone.
  const [statusFilter, setStatusFilter] = usePersistedState(
    'salaries.status',
    'active'
  );
  const [departments, setDepartments] = useState([]);
  const [page, setPage] = usePersistedState('salaries.page', 1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [salaryStats, setSalaryStats] = useState({
    total_usd: 0,
    total_pkr: 0,
    active_records: 0,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSalaryId, setDeleteSalaryId] = useState(null);

  // Debounce search term to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Only the latest load may fill the list: a page click or a filter change while one
  // is in flight starts another, and a late response would overwrite the newer one.
  // Nor is the response's page written back: it is only ever the page asked for, and
  // writing it back let two loads in flight flip the page between them, refetching on
  // every flip.
  const beginRequest = useLatestRequest();

  useEffect(() => {
    fetchSalaries();
    fetchSalaryStats();
    // eslint-disable-next-line
  }, [
    page,
    limit,
    debouncedSearchTerm,
    currencyFilter,
    employmentTypeFilter,
    departmentFilter,
    statusFilter,
  ]);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await api.get('/api/salaries/departments');
        setDepartments(res.data.data || []);
      } catch (err) {
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, []);

  const fetchSalaries = async () => {
    const isCurrent = beginRequest();
    try {
      setSearchLoading(true);

      const params = buildQueryParams({
        page,
        limit,
        search: debouncedSearchTerm,
        currency: currencyFilter,
        employment_type: employmentTypeFilter,
        department_id: departmentFilter,
        status: statusFilter,
      });
      const response = await api.get('/api/salaries', { params });
      if (!isCurrent()) return;
      setSalaries(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      if (!isCurrent()) return;
      // Fixed ids: StrictMode loads twice in dev, and each failure would toast again.
      if (error.response?.status === 403) {
        toastErrorOnce('You do not have permission to view salary records');
      } else if (error.response?.status === 401) {
        toastErrorOnce('Please log in again to continue');
      } else {
        toastErrorOnce('Failed to load salaries. Please try again.');
      }
      setSalaries([]);
      setTotal(0);
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setSearchLoading(false);
      }
    }
  };

  const fetchSalaryStats = async () => {
    try {
      const res = await api.get('/api/salaries/statistics/overview');
      setSalaryStats({
        total_usd: res.data.data.total_usd_salaries?.total || 0,
        total_pkr: res.data.data.total_pkr_salaries?.total || 0,
        active_records: res.data.data.total_employees?.count || 0,
      });
    } catch (error) {
      if (error.response?.status === 403) {
        setSalaryStats({ total_usd: 0, total_pkr: 0, active_records: 0 });
      } else {
        setSalaryStats({ total_usd: 0, total_pkr: 0, active_records: 0 });
      }
    }
  };

  const totalPages = Math.ceil(total / limit);

  const handleDelete = (id) => {
    setDeleteSalaryId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/salaries/${deleteSalaryId}`);
      setSalaries(salaries.filter((s) => s.id !== deleteSalaryId));
      toast.success('Salary record deleted successfully');
    } catch (err) {
      toast.error('Failed to delete salary record');
    }
    setShowDeleteModal(false);
    setDeleteSalaryId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div role="status" aria-label="Loading salaries" className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  let filteredSalaries = salaries;
  if (
    !hasPermission('salaries', 'read_all') &&
    !hasPermission('salaries', 'read_department')
  ) {
    // Only has read_own, filter to own salary
    filteredSalaries = salaries.filter(
      (s) => s.employee_id === user?.employee_id,
    );
  }

  const columns = [
    {
      key: 'employee',
      header: 'Employee',
      render: (salary) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {salary.first_name} {salary.last_name}
          </div>
          <div className="text-sm text-gray-500">{salary.emp_id}</div>
          <div className="text-xs text-gray-400">
            {salary.department_name || 'N/A'}
          </div>
        </div>
      ),
    },
    {
      key: 'currency',
      header: 'Currency',
      render: (salary) => (
        <span
          data-testid="salaries-cell-currency"
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            salary.currency === 'USD'
              ? 'bg-green-100 text-green-800'
              : 'bg-blue-100 text-blue-800'
          }`}
        >
          {salary.currency}
        </span>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (salary) => (
        <div
          data-testid="salaries-cell-amount"
          className="text-sm font-medium text-gray-900"
        >
          {formatCurrency(salary.amount, salary.currency)}
        </div>
      ),
    },
    {
      key: 'effective_date',
      header: 'Effective Date',
      render: (salary) => (
        <div className="text-sm text-gray-900">
          {salary.effective_date ? (
            formatDateOnly(salary.effective_date)
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'payment_method',
      header: 'Payment Method',
      render: (salary) => (
        <div className="text-sm text-gray-900">
          {salary.payment_method_name || (
            <span className="text-gray-400">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (salary) =>
        salary.employment_status ? (
          <span
            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClasses(
              salary.employment_status,
              'employee',
            )}`}
          >
            {formatStatusText(salary.employment_status)}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      cellClassName: 'font-medium',
      render: (salary) => (
        <div className="flex items-center justify-end space-x-2">
          <Link
            to={`/salaries/${salary.id}`}
            className="text-primary-600 hover:text-primary-900 p-1"
            title="View Details & History"
          >
            <Eye className="h-4 w-4" />
          </Link>
          <Link
            to={
              canEdit
                ? `/salaries/${salary.id}/edit`
                : '#'
            }
            aria-disabled={!canEdit}
            className={`p-1 ${
              canEdit
                ? 'text-warning-600 hover:text-warning-900 cursor-pointer'
                : 'text-gray-400 cursor-not-allowed'
            }`}
            title={
              canEdit
                ? 'Edit Salary'
                : 'You do not have permission to edit salaries'
            }
            onClick={(e) => {
              if (!canEdit) {
                e.preventDefault();
              }
            }}
          >
            <Edit className="h-4 w-4" />
          </Link>
          <button
            onClick={() => {
              if (hasPermission('salaries', 'delete')) {
                handleDelete(salary.id);
              }
            }}
            className={`p-1 ${
              hasPermission('salaries', 'delete')
                ? 'text-danger-600 hover:text-danger-900 cursor-pointer'
                : 'text-gray-400 cursor-not-allowed'
            }`}
            title={
              hasPermission('salaries', 'delete')
                ? 'Delete'
                : 'You do not have permission to delete salaries'
            }
            disabled={!hasPermission('salaries', 'delete')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Salaries</h1>
          <p className="text-gray-600">Manage employee salary information</p>
        </div>
        {hasPermission('salaries', 'create') && (
          <Link
            to="/salaries/add"
            data-testid="salaries-add-btn"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Salary Record
          </Link>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <SummaryCard
          variant="stat"
          icon={DollarSign}
          iconColor="text-success-600"
          label="Total USD Salaries"
          value={`$${Number.parseFloat(salaryStats.total_usd).toLocaleString()}`}
        />

        <SummaryCard
          variant="stat"
          icon={DollarSign}
          iconColor="text-warning-600"
          label="Total PKR Salaries"
          value={`₨${Number.parseFloat(salaryStats.total_pkr).toLocaleString()}`}
        />

        <SummaryCard
          variant="stat"
          icon={TrendingUp}
          iconColor="text-primary-600"
          label="Active Records"
          value={salaryStats.active_records}
        />
      </div>

      {/* Filters */}
      <CFiltersBar
        searchTestId="salaries-search"
        clearTestId="salaries-clear-filters"
        showSearch
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search employees..."
        searchLoading={searchLoading}
        gridColsClass="md:grid-cols-6"
        filters={[
          {
            key: 'currency',
            testId: 'salaries-currency-filter',
            label: 'Currency',
            value: currencyFilter,
            onChange: (e) => setCurrencyFilter(e.target.value),
            options: [
              { value: '', label: 'All Currencies' },
              { value: 'USD', label: 'USD' },
              { value: 'PKR', label: 'PKR' },
            ],
          },
          {
            key: 'employmentType',
            testId: 'salaries-employment-type-filter',
            label: 'Employment Type',
            value: employmentTypeFilter,
            onChange: (e) => setEmploymentTypeFilter(e.target.value),
            options: [
              { value: '', label: 'All Types' },
              ...EMPLOYMENT_TYPES.map((type) => ({ value: type, label: type })),
            ],
          },
          {
            key: 'status',
            testId: 'salaries-status-filter',
            label: 'Employee Status',
            value: statusFilter,
            onChange: (e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            },
            options: EMPLOYEE_STATUSES.map(({ value, label }) => ({
              value: value === '' ? 'all' : value,
              label,
            })),
          },
          {
            key: 'department',
            testId: 'salaries-department-filter',
            label: 'Department',
            value: departmentFilter,
            onChange: (e) => setDepartmentFilter(e.target.value),
            options: [
              { value: '', label: 'All Departments' },
              ...departments.map((dept) => ({
                value: dept.id,
                label: dept.name,
              })),
            ],
          },
        ]}
        onClear={() => {
          setSearchTerm('');
          setDebouncedSearchTerm('');
          setCurrencyFilter('');
          setEmploymentTypeFilter('');
          setDepartmentFilter('');
          // Back to the default view, not to 'all'.
          setStatusFilter('active');
        }}
      />

      {/* Salaries List */}
      <CTable
        columns={columns}
        data={filteredSalaries}
        rowKey="id"
        getRowProps={(salary) => ({
          'data-testid': `salaries-row-${salary.id}`,
        })}
        header={
          <h3 className="text-lg font-medium text-gray-900">
            {total} Salary Record{total !== 1 ? 's' : ''}
          </h3>
        }
        emptyMessage={
          !hasPermission('salaries', 'read_all') &&
          !hasPermission('salaries', 'read_department') &&
          !hasPermission('salaries', 'read_own') ? (
            <>
              <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No access
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                You do not have access to any salary records.
              </p>
            </>
          ) : (
            <>
              <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No salary records found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ||
                currencyFilter ||
                employmentTypeFilter ||
                departmentFilter
                  ? 'Try adjusting your filters to see more results.'
                  : 'No salary records have been created yet.'}
              </p>
            </>
          )
        }
      />

      {/* Only show pagination if there are multiple pages */}
      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Delete Salary Modal */}
      {showDeleteModal && (
        <CModal
          onClose={() => setShowDeleteModal(false)}
          labelledBy="delete-salary-title"
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setShowDeleteModal(false)}
          >
            &times;
          </button>
          <h2 id="delete-salary-title" className="text-xl font-bold mb-4 text-danger-700">
            Delete Salary Record
          </h2>
          <p className="mb-4 text-gray-700">
            Are you sure you want to delete this salary record? This action
            cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 rounded bg-danger-600 text-white hover:bg-danger-700 font-semibold"
              onClick={confirmDelete}
            >
              Delete
            </button>
          </div>
        </CModal>
      )}
    </div>
  );
};

export default Salaries;
